import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import {
  XMarkIcon,
  ArrowPathIcon,
  PlusIcon,
  TrashIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import ingestApi from "../../../lib/ingestApi";

const TICKET_TYPE_OPTIONS = [
  { label: "일반승차권", value: "normal" },
  { label: "바우처", value: "voucher" },
  { label: "홈티켓", value: "homeTicket" },
  { label: "SRT", value: "srt" },
  { label: "특단", value: "special" },
];

const pageStatusColor = (status, isActive) => {
  if (isActive) return "bg-blue-600 text-white";
  switch (status) {
    case "committed":
      return "bg-green-100 text-green-700 border border-green-300";
    case "staged":
      return "bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200";
    case "failed":
    case "commit_failed":
      return "bg-red-100 text-red-700 border border-red-300";
    case "processing":
    case "retrying":
    case "queued":
      return "bg-yellow-100 text-yellow-700 border border-yellow-300";
    default:
      return "bg-gray-100 text-gray-600 border border-gray-200";
  }
};

const formatStatus = (status) => {
  const labels = {
    queued: "대기",
    processing: "처리 중",
    retrying: "재시도 중",
    staged: "완료",
    failed: "실패",
    committed: "등록됨",
    commit_failed: "등록 실패",
  };
  return labels[status] || status || "-";
};

/** Convert OCR payload to form data structure */
const ocrPayloadToFormData = (payload) => {
  if (!payload || !payload.ticketItemList || payload.ticketItemList.length === 0) {
    return [createEmptyTrainData()];
  }

  return payload.ticketItemList.map((item) => {
    const depParts = (item.departureTime || "").split(" ");
    const arrParts = (item.arrivalTime || "").split(" ");

    return {
      reservation_number: item.reservationNumber || "",
      ticket_type: payload.ticketType || "normal",
      departure_date: depParts[0] || "",
      train_code: item.trainCode || "",
      departure_station: item.departureStation || "",
      arrival_station: item.arrivalStation || "",
      departure_time: depParts[1] || "",
      arrival_time: arrParts[1] || "",
      seat_info_list: (item.seatInfoList && item.seatInfoList.length > 0)
        ? item.seatInfoList.map((s) => ({
            train_car_number: s.trainCarNumber || "",
            seat_code: s.seatCode || "",
          }))
        : [{ train_car_number: "", seat_code: "" }],
    };
  });
};

/** Convert form data back to OCR payload format */
const formDataToOcrPayload = (trainList) => {
  if (!trainList || trainList.length === 0) return {};

  const ticketType = trainList[0].ticket_type || "normal";

  return {
    ticketType,
    ticketItemList: trainList.map((train) => ({
      reservationNumber: train.reservation_number || "",
      trainCode: train.train_code || "",
      departureStation: train.departure_station || "",
      arrivalStation: train.arrival_station || "",
      departureTime: `${train.departure_date || ""} ${train.departure_time || ""}`.trim(),
      arrivalTime: `${train.departure_date || ""} ${train.arrival_time || ""}`.trim(),
      seatInfoList: (train.seat_info_list || []).map((s) => ({
        trainCarNumber: s.train_car_number || "",
        seatCode: s.seat_code || "",
      })),
    })),
  };
};

const createEmptyTrainData = () => ({
  reservation_number: "",
  ticket_type: "normal",
  departure_date: "",
  train_code: "",
  departure_station: "",
  arrival_station: "",
  departure_time: "",
  arrival_time: "",
  seat_info_list: [{ train_car_number: "", seat_code: "" }],
});

// 수정 저장 / 등록 확정 시 검증할 필수 필드 (formDataMap의 field key 기준)
const REQUIRED_FIELDS = [
  { key: "reservation_number", label: "예약번호" },
  { key: "train_code", label: "열차번호" },
  { key: "departure_station", label: "출발역" },
  { key: "arrival_station", label: "도착역" },
  { key: "departure_date", label: "출발일" },
  { key: "departure_time", label: "출발시간" },
  { key: "arrival_time", label: "도착시간" },
];

/** 현재 페이지 내 모든 열차의 필수값 검증 */
const validateTrainList = (trainList) => {
  const errorMap = {};
  const missingLabels = new Set();
  let firstErrorTrainIndex = -1;
  (trainList || []).forEach((train, idx) => {
    REQUIRED_FIELDS.forEach(({ key, label }) => {
      const value = train?.[key];
      const isEmpty =
        value === undefined ||
        value === null ||
        (typeof value === "string" && value.trim() === "");
      if (isEmpty) {
        if (!errorMap[idx]) errorMap[idx] = {};
        errorMap[idx][key] = true;
        missingLabels.add(label);
        if (firstErrorTrainIndex === -1) firstErrorTrainIndex = idx;
      }
    });
  });
  // alert에 표시할 "첫 번째 에러 열차의" 누락 라벨 목록 (선언 순서 유지)
  const firstTrainMissingLabels =
    firstErrorTrainIndex >= 0
      ? REQUIRED_FIELDS
          .filter(({ key }) => errorMap[firstErrorTrainIndex]?.[key])
          .map(({ label }) => label)
      : [];
  return {
    errorMap,
    missingLabels: Array.from(missingLabels),
    firstErrorTrainIndex,
    firstTrainMissingLabels,
  };
};

// commit 종결 상태 폴링 설정
const COMMIT_POLL_INTERVAL_MS = 800;
const COMMIT_POLL_TIMEOUT_MS = 30000;
const TERMINAL_COMMIT_STATUSES = new Set([
  "committed",
  "partial_committed",
  "commit_failed",
]);

export default function SessionReviewModal({ sessionId, onClose, onCommit }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [activeTrainIndex, setActiveTrainIndex] = useState(0);

  // Form state per page: { [pageId]: trainList[] }
  const [formDataMap, setFormDataMap] = useState({});
  // Track which pages have unsaved edits
  const [dirtyPages, setDirtyPages] = useState(new Set());
  // 필드별 에러 상태: { [pageId]: { [trainIndex]: { [fieldKey]: true } } }
  const [fieldErrorMap, setFieldErrorMap] = useState({});

  const loadPages = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await ingestApi.getPages(sessionId);
      const items = response.data?.items || [];
      setPages(items);

      // Initialize form data for each page
      const dataMap = {};
      items.forEach((page) => {
        dataMap[page.id] = ocrPayloadToFormData(page.ocr_payload);
      });
      setFormDataMap(dataMap);

      // Select first non-committed page, or first page
      const firstEditable = items.findIndex(
        (p) => p.ocr_status !== "committed"
      );
      setActivePageIndex(firstEditable >= 0 ? firstEditable : 0);
      setActiveTrainIndex(0);
      setDirtyPages(new Set());
    } catch (err) {
      setError("페이지 목록을 불러오는 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  // commit 종결 상태가 될 때까지 getSession 폴링 (상한: COMMIT_POLL_TIMEOUT_MS)
  const pollSessionUntilCommitDone = async (sid) => {
    const start = Date.now();
    while (Date.now() - start < COMMIT_POLL_TIMEOUT_MS) {
      await new Promise((r) => setTimeout(r, COMMIT_POLL_INTERVAL_MS));
      try {
        const res = await ingestApi.getSession(sid);
        const status = res?.data?.status;
        if (TERMINAL_COMMIT_STATUSES.has(status)) {
          return { ok: true, status, data: res.data };
        }
      } catch (_) {
        // 일시적 네트워크 오류는 무시하고 다음 폴링 시도, 최종은 타임아웃에서 처리
      }
    }
    return { ok: false, status: "timeout" };
  };

  // 폴링 중에는 모달 닫기를 차단
  const handleClose = () => {
    if (actionLoading === "commit-all") return;
    if (onClose) onClose();
  };

  const activePage = pages[activePageIndex] || null;
  const activePageId = activePage?.id;
  const trainList = activePageId ? (formDataMap[activePageId] || []) : [];
  const trainData = trainList[activeTrainIndex] || null;

  // --- Form handlers ---

  const updateTrainField = (field, value) => {
    if (!activePageId) return;
    setFormDataMap((prev) => {
      const pageTrains = [...(prev[activePageId] || [])];
      if (field === "ticket_type") {
        // Apply ticket_type to all trains in this page
        pageTrains.forEach((t, i) => {
          pageTrains[i] = { ...t, ticket_type: value };
        });
      } else {
        pageTrains[activeTrainIndex] = {
          ...pageTrains[activeTrainIndex],
          [field]: value,
        };
      }
      return { ...prev, [activePageId]: pageTrains };
    });
    setDirtyPages((prev) => new Set(prev).add(activePageId));

    // 값이 채워지면 해당 (페이지-열차-필드) 에러 즉시 해제
    if (field === "ticket_type") return;
    const hasValue =
      typeof value === "string" ? value.trim() !== "" : value !== undefined && value !== null;
    if (!hasValue) return;
    setFieldErrorMap((prev) => {
      const pageErrors = prev[activePageId];
      if (!pageErrors) return prev;
      const trainErrors = pageErrors[activeTrainIndex];
      if (!trainErrors || !trainErrors[field]) return prev;
      const newTrainErrors = { ...trainErrors };
      delete newTrainErrors[field];
      const newPageErrors = { ...pageErrors };
      if (Object.keys(newTrainErrors).length === 0) {
        delete newPageErrors[activeTrainIndex];
      } else {
        newPageErrors[activeTrainIndex] = newTrainErrors;
      }
      const next = { ...prev };
      if (Object.keys(newPageErrors).length === 0) {
        delete next[activePageId];
      } else {
        next[activePageId] = newPageErrors;
      }
      return next;
    });
  };

  const updateSeatField = (seatIndex, field, value) => {
    if (!activePageId) return;
    if (field === "train_car_number" && value) {
      value = value.replace(/[^0-9]/g, "");
    }
    setFormDataMap((prev) => {
      const pageTrains = [...(prev[activePageId] || [])];
      const train = { ...pageTrains[activeTrainIndex] };
      const seats = [...train.seat_info_list];
      seats[seatIndex] = { ...seats[seatIndex], [field]: value };
      train.seat_info_list = seats;
      pageTrains[activeTrainIndex] = train;
      return { ...prev, [activePageId]: pageTrains };
    });
    setDirtyPages((prev) => new Set(prev).add(activePageId));
  };

  const addSeat = () => {
    if (!activePageId) return;
    setFormDataMap((prev) => {
      const pageTrains = [...(prev[activePageId] || [])];
      const train = { ...pageTrains[activeTrainIndex] };
      train.seat_info_list = [...train.seat_info_list, { train_car_number: "", seat_code: "" }];
      pageTrains[activeTrainIndex] = train;
      return { ...prev, [activePageId]: pageTrains };
    });
    setDirtyPages((prev) => new Set(prev).add(activePageId));
  };

  const removeSeat = (seatIndex) => {
    if (!activePageId) return;
    setFormDataMap((prev) => {
      const pageTrains = [...(prev[activePageId] || [])];
      const train = { ...pageTrains[activeTrainIndex] };
      if (train.seat_info_list.length <= 1) return prev;
      const seats = [...train.seat_info_list];
      seats.splice(seatIndex, 1);
      train.seat_info_list = seats;
      pageTrains[activeTrainIndex] = train;
      return { ...prev, [activePageId]: pageTrains };
    });
    setDirtyPages((prev) => new Set(prev).add(activePageId));
  };

  const addTrain = () => {
    if (!activePageId) return;
    setFormDataMap((prev) => {
      const pageTrains = [...(prev[activePageId] || [])];
      const newTrain = createEmptyTrainData();
      if (pageTrains.length > 0) {
        newTrain.ticket_type = pageTrains[0].ticket_type;
      }
      pageTrains.push(newTrain);
      return { ...prev, [activePageId]: pageTrains };
    });
    setDirtyPages((prev) => new Set(prev).add(activePageId));
    setActiveTrainIndex(trainList.length); // will be the new last index
  };

  const removeTrain = (index) => {
    if (!activePageId) return;
    setFormDataMap((prev) => {
      const pageTrains = [...(prev[activePageId] || [])];
      if (pageTrains.length <= 1) return prev;
      pageTrains.splice(index, 1);
      return { ...prev, [activePageId]: pageTrains };
    });
    setDirtyPages((prev) => new Set(prev).add(activePageId));
    if (activeTrainIndex >= trainList.length - 1) {
      setActiveTrainIndex(Math.max(0, trainList.length - 2));
    }
  };

  // --- Action handlers ---

  const handleSavePage = async () => {
    if (!activePageId) return;
    const pageTrains = formDataMap[activePageId] || [];

    // 필수값 검증 (현재 페이지의 모든 열차)
    const { errorMap, firstErrorTrainIndex, firstTrainMissingLabels } =
      validateTrainList(pageTrains);
    if (firstErrorTrainIndex >= 0) {
      setFieldErrorMap((prev) => ({ ...prev, [activePageId]: errorMap }));
      // 첫 번째 에러 열차 탭으로 자동 이동
      if (firstErrorTrainIndex !== activeTrainIndex) {
        setActiveTrainIndex(firstErrorTrainIndex);
      }
      const fieldText =
  firstTrainMissingLabels.length > 0
    ? firstTrainMissingLabels.join(", ")
    : "필수값";

window.alert(
  `입력되지 않은 필수값이 있습니다.\n\n열차 #${
    firstErrorTrainIndex + 1
  }의 ${fieldText}을 입력해주세요.`
);
      return;
    }
    setFieldErrorMap((prev) => {
      if (!prev[activePageId]) return prev;
      const next = { ...prev };
      delete next[activePageId];
      return next;
    });

    const payload = formDataToOcrPayload(pageTrains);
    setActionLoading("save");
    setError(null);
    try {
      await ingestApi.updatePage(sessionId, activePageId, payload);
      setPages((prev) =>
        prev.map((p) =>
          p.id === activePageId
            ? { ...p, ocr_payload: payload, ocr_status: "staged", error_message: null }
            : p
        )
      );
      setDirtyPages((prev) => {
        const next = new Set(prev);
        next.delete(activePageId);
        return next;
      });
    } catch (err) {
      setError("수정 저장에 실패했습니다.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCommitPage = async () => {
    if (!activePageId) return;

    // 필수값 검증 (현재 페이지의 모든 열차)
    const pageTrains = formDataMap[activePageId] || [];
    const { errorMap, firstErrorTrainIndex, firstTrainMissingLabels } =
      validateTrainList(pageTrains);
    if (firstErrorTrainIndex >= 0) {
      setFieldErrorMap((prev) => ({ ...prev, [activePageId]: errorMap }));
      // 첫 번째 에러 열차 탭으로 자동 이동
      if (firstErrorTrainIndex !== activeTrainIndex) {
        setActiveTrainIndex(firstErrorTrainIndex);
      }
      const fieldText =
  firstTrainMissingLabels.length > 0
    ? firstTrainMissingLabels.join(", ")
    : "필수값";

window.alert(
  `입력되지 않은 필수값이 있습니다.\n\n열차 #${
    firstErrorTrainIndex + 1
  }의 ${fieldText}을 입력해주세요.`
);
      return;
    }
    setFieldErrorMap((prev) => {
      if (!prev[activePageId]) return prev;
      const next = { ...prev };
      delete next[activePageId];
      return next;
    });

    setActionLoading("commit-page");
    setError(null);
    try {
      await ingestApi.commitSession(sessionId, [activePageId]);
      setPages((prev) =>
        prev.map((p) =>
          p.id === activePageId ? { ...p, ocr_status: "committed" } : p
        )
      );
      if (onCommit) onCommit();
      // Move to next uncommitted page
      const nextIndex = pages.findIndex(
        (p, i) => i > activePageIndex && p.ocr_status !== "committed"
      );
      if (nextIndex >= 0) {
        setActivePageIndex(nextIndex);
        setActiveTrainIndex(0);
      }
    } catch (err) {
      setError("등록 확정에 실패했습니다.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCommitAll = async () => {
    const stagedPageIds = pages
      .filter((p) => p.ocr_status === "staged" && !p.committed_ticket_id)
      .map((p) => p.id);
    if (stagedPageIds.length === 0) {
      setError("등록할 수 있는 페이지가 없습니다.");
      return;
    }

    // 폴링 종료 전까지 actionLoading을 유지 → 모든 액션 버튼 자동 disabled
    setActionLoading("commit-all");
    setError(null);

    // 1) commit 요청 (200 = "committing" 큐잉 성공)
    try {
      await ingestApi.commitSession(sessionId, stagedPageIds);
    } catch (err) {
      setError("전체 등록 요청에 실패했습니다.");
      toast.error("전체 등록 요청에 실패했습니다.");
      setActionLoading(null);
      return;
    }

    // 2) BE 워커가 끝낼 때까지 세션 상태 폴링
    const result = await pollSessionUntilCommitDone(sessionId);

    // 3) 폴링 결과와 무관하게 페이지/세션 리스트 최신화
    try {
      await loadPages();
    } catch (_) {
      // 토스트로 별도 안내
    }
    if (onCommit) onCommit();

    // 4) 결과별 분기
    if (!result.ok) {
      // 타임아웃: 모달은 열어두고 사용자가 다시 확인할 수 있게 안내
      toast.info("등록이 지연되고 있습니다. 잠시 후 진행 상황 탭에서 확인해주세요.");
      setActionLoading(null);
      return;
    }

    const committedCount = result.data?.committed_pages ?? 0;
    if (result.status === "committed") {
      toast.success(`${committedCount || stagedPageIds.length}건 등록 완료`);
      setActionLoading(null);
      if (onClose) onClose();
    } else if (result.status === "partial_committed") {
      toast.warn(`${committedCount}건 등록 완료 / 일부 페이지 실패`);
      setError("일부 페이지 등록에 실패했습니다. 실패한 페이지를 확인해주세요.");
      setActionLoading(null);
    } else {
      // commit_failed
      toast.error("전체 등록에 실패했습니다.");
      setError("전체 등록에 실패했습니다.");
      setActionLoading(null);
    }
  };

  const handleRetryPage = async () => {
    if (!activePageId) return;
    setActionLoading("retry");
    setError(null);
    try {
      await ingestApi.retryPage(sessionId, activePageId);
      setPages((prev) =>
        prev.map((p) =>
          p.id === activePageId ? { ...p, ocr_status: "queued", error_message: null } : p
        )
      );
    } catch (err) {
      setError("재시도 요청에 실패했습니다.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeletePage = async () => {
    if (!activePageId) return;
    if (!window.confirm("이 페이지를 삭제하시겠습니까?")) return;
    setActionLoading("delete");
    setError(null);
    try {
      await ingestApi.deletePage(sessionId, activePageId);
      const newPages = pages.filter((p) => p.id !== activePageId);
      setPages(newPages);
      setFormDataMap((prev) => {
        const next = { ...prev };
        delete next[activePageId];
        return next;
      });
      if (activePageIndex >= newPages.length) {
        setActivePageIndex(Math.max(0, newPages.length - 1));
      }
      setActiveTrainIndex(0);
    } catch (err) {
      setError("페이지 삭제에 실패했습니다.");
    } finally {
      setActionLoading(null);
    }
  };

  // --- Page tab switch ---
  const handlePageSwitch = (index) => {
    setActivePageIndex(index);
    setActiveTrainIndex(0);
  };

  // --- Counts ---
  const stagedCount = pages.filter(
    (p) => p.ocr_status === "staged" && !p.committed_ticket_id
  ).length;
  const committedCount = pages.filter((p) => p.ocr_status === "committed").length;

  const isPageEditable = activePage && !["committed", "processing", "queued", "retrying"].includes(activePage.ocr_status);
  const isPageCommittable = activePage?.ocr_status === "staged" && !activePage.committed_ticket_id;

  // --- Render ---

  const inputClass =
    "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm";
  const errorInputClass =
    "w-full px-3 py-2 border border-red-500 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 text-sm";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  // 현재 활성 페이지/열차 기준 필드별 에러 여부
  const currentFieldErrors =
    (activePageId && fieldErrorMap[activePageId]?.[activeTrainIndex]) || {};
  const fieldClass = (fieldKey) =>
    currentFieldErrors[fieldKey] ? errorInputClass : inputClass;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[1400px] max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">
              승차권 등록 검수
            </h2>
            <span className="text-sm text-gray-500">
              {committedCount}/{pages.length} 등록됨
            </span>
          </div>
          <button
            onClick={handleClose}
            disabled={actionLoading === "commit-all"}
            className={`text-gray-500 hover:text-gray-700 ${
              actionLoading === "commit-all" ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Page Tabs */}
        {!loading && pages.length > 0 && (
          <div className="flex items-center gap-2 px-4 py-3 border-b bg-gray-50 overflow-x-auto shrink-0">
            <span className="text-sm font-medium text-gray-600 mr-1">페이지:</span>
            {pages.map((page, index) => (
              <button
                key={page.id}
                onClick={() => handlePageSwitch(index)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${pageStatusColor(
                  page.ocr_status,
                  index === activePageIndex
                )}`}
              >
                #{index + 1}
                {page.ocr_status === "committed" && <CheckIcon className="h-3.5 w-3.5" />}
                {(page.ocr_status === "failed" || page.ocr_status === "commit_failed") && (
                  <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                )}
              </button>
            ))}
            <span className={`ml-auto text-xs px-2 py-1 rounded-full ${
              activePage ? (
                activePage.ocr_status === "committed" ? "bg-green-100 text-green-700" :
                activePage.ocr_status === "staged" ? "bg-blue-100 text-blue-700" :
                activePage.ocr_status === "failed" ? "bg-red-100 text-red-700" :
                "bg-gray-100 text-gray-600"
              ) : "bg-gray-100 text-gray-600"
            }`}>
              {activePage ? formatStatus(activePage.ocr_status) : "-"}
            </span>
            {activePage && ["failed", "commit_failed"].includes(activePage.ocr_status) && (
              <button
                onClick={handleRetryPage}
                disabled={!!actionLoading}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-yellow-700 bg-yellow-100 rounded hover:bg-yellow-200"
              >
                <ArrowPathIcon className="h-3.5 w-3.5" />
                재시도
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="text-center py-16 text-gray-500">페이지 로딩 중...</div>
          ) : pages.length === 0 ? (
            <div className="text-center py-16 text-gray-500">페이지가 없습니다.</div>
          ) : !activePage ? (
            <div className="text-center py-16 text-gray-500">페이지를 선택해주세요.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 h-full">
              {/* Left: Image Preview */}
              <div className="border-r p-4 overflow-auto">
                <label className={labelClass}>승차권 이미지</label>
                <div className="mt-1 flex justify-center px-4 pt-4 pb-4 border-2 border-gray-300 border-dashed rounded-md bg-gray-50">
                  {activePage.page_image_url ? (
                    <img
                      src={activePage.page_image_url}
                      alt={`Page ${activePage.id}`}
                      className="max-w-full max-h-[70vh] object-contain"
                    />
                  ) : (
                    <div className="text-gray-400 text-sm py-12">이미지 없음</div>
                  )}
                </div>
              </div>

              {/* Right: Form */}
              <div className="p-4 overflow-auto">
                {/* Error message for this page */}
                {activePage.error_message && (
                  <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 text-red-700 text-sm rounded-md border border-red-200">
                    <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                    {activePage.error_message}
                  </div>
                )}

                {activePage.ocr_status === "committed" ? (
                  <div className="text-center py-12">
                    <CheckIcon className="h-12 w-12 text-green-500 mx-auto mb-3" />
                    <p className="text-green-700 font-medium">이 페이지는 이미 등록되었습니다.</p>
                    {activePage.committed_ticket_id && (
                      <p className="text-sm text-gray-500 mt-1">티켓 #{activePage.committed_ticket_id}</p>
                    )}
                  </div>
                ) : ["processing", "queued", "retrying"].includes(activePage.ocr_status) ? (
                  <div className="text-center py-12">
                    <ArrowPathIcon className="h-12 w-12 text-yellow-500 mx-auto mb-3 animate-spin" />
                    <p className="text-yellow-700 font-medium">처리 중입니다...</p>
                  </div>
                ) : (
                  <>
                    {/* Train Tabs */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">열차:</span>
                        {trainList.map((_, tIdx) => (
                          <button
                            key={tIdx}
                            onClick={() => setActiveTrainIndex(tIdx)}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                              tIdx === activeTrainIndex
                                ? "bg-blue-600 text-white shadow"
                                : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
                            }`}
                          >
                            #{tIdx + 1}
                          </button>
                        ))}
                        <button
                          onClick={addTrain}
                          className="inline-flex items-center gap-0.5 px-2 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md"
                        >
                          <PlusIcon className="h-4 w-4" />
                          추가
                        </button>
                      </div>
                      {trainList.length > 1 && (
                        <button
                          onClick={() => removeTrain(activeTrainIndex)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                          열차 삭제
                        </button>
                      )}
                    </div>

                    {/* Form Fields */}
                    {trainData && (
                      <div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className={labelClass}>예약번호</label>
                            <input
                              type="text"
                              value={trainData.reservation_number}
                              onChange={(e) => updateTrainField("reservation_number", e.target.value)}
                              className={fieldClass("reservation_number")}
                            />
                          </div>

                          <div>
                            <label className={labelClass}>티켓 종류</label>
                            <select
                              value={trainData.ticket_type}
                              onChange={(e) => updateTrainField("ticket_type", e.target.value)}
                              className={inputClass}
                            >
                              {TICKET_TYPE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className={labelClass}>출발일</label>
                            <input
                              type="date"
                              value={trainData.departure_date}
                              onChange={(e) => updateTrainField("departure_date", e.target.value)}
                              className={fieldClass("departure_date")}
                            />
                          </div>

                          <div>
                            <label className={labelClass}>열차번호</label>
                            <input
                              type="text"
                              value={trainData.train_code}
                              onChange={(e) => updateTrainField("train_code", e.target.value)}
                              className={fieldClass("train_code")}
                            />
                          </div>

                          <div>
                            <label className={labelClass}>출발역</label>
                            <input
                              type="text"
                              value={trainData.departure_station}
                              onChange={(e) => updateTrainField("departure_station", e.target.value)}
                              className={fieldClass("departure_station")}
                            />
                          </div>

                          <div>
                            <label className={labelClass}>도착역</label>
                            <input
                              type="text"
                              value={trainData.arrival_station}
                              onChange={(e) => updateTrainField("arrival_station", e.target.value)}
                              className={fieldClass("arrival_station")}
                            />
                          </div>

                          <div>
                            <label className={labelClass}>출발시간</label>
                            <input
                              type="time"
                              value={trainData.departure_time}
                              onChange={(e) => updateTrainField("departure_time", e.target.value)}
                              className={fieldClass("departure_time")}
                            />
                          </div>

                          <div>
                            <label className={labelClass}>도착시간</label>
                            <input
                              type="time"
                              value={trainData.arrival_time}
                              onChange={(e) => updateTrainField("arrival_time", e.target.value)}
                              className={fieldClass("arrival_time")}
                            />
                          </div>
                        </div>

                        {/* Seat Info Table */}
                        <div className="mt-4">
                          <div className="flex justify-between items-center mb-2">
                            <label className="text-sm font-medium text-gray-700">좌석 정보</label>
                            <button
                              type="button"
                              onClick={addSeat}
                              className="inline-flex items-center gap-1 px-3 py-1 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm"
                            >
                              <PlusIcon className="h-4 w-4" />
                              좌석 추가
                            </button>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase" style={{ width: "30%" }}>
                                    호차
                                  </th>
                                  <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase" style={{ width: "50%" }}>
                                    좌석
                                  </th>
                                  <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase" style={{ width: "20%" }}>
                                    삭제
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {trainData.seat_info_list.map((seat, sIdx) => (
                                  <tr key={sIdx}>
                                    <td className="px-3 py-2">
                                      <input
                                        type="text"
                                        placeholder="호차"
                                        value={seat.train_car_number}
                                        onChange={(e) => updateSeatField(sIdx, "train_car_number", e.target.value)}
                                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                      />
                                    </td>
                                    <td className="px-3 py-2">
                                      <input
                                        type="text"
                                        placeholder="좌석번호"
                                        value={seat.seat_code}
                                        onChange={(e) => updateSeatField(sIdx, "seat_code", e.target.value)}
                                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                      <button
                                        type="button"
                                        onClick={() => removeSeat(sIdx)}
                                        className="text-red-600 hover:text-red-900 text-sm"
                                        disabled={trainData.seat_info_list.length <= 1}
                                      >
                                        삭제
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Error toast */}
        {error && (
          <div className="mx-4 mb-2 flex items-center gap-2 p-3 bg-red-50 text-red-700 text-sm rounded-md border border-red-200">
            <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t bg-gray-50 shrink-0">
          <div className="flex items-center gap-2">
            {activePage && isPageEditable && (
              <button
                onClick={handleDeletePage}
                disabled={!!actionLoading}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md border border-red-200"
              >
                <TrashIcon className="h-4 w-4" />
                페이지 삭제
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClose}
              disabled={actionLoading === "commit-all"}
              className={`px-4 py-2 rounded-md text-sm ${
                actionLoading === "commit-all"
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              닫기
            </button>
            {isPageEditable && (
              <button
                onClick={handleSavePage}
                disabled={!!actionLoading || !dirtyPages.has(activePageId)}
                className={`px-4 py-2 rounded-md text-sm ${
                  dirtyPages.has(activePageId)
                    ? "bg-green-600 text-white hover:bg-green-700"
                    : "bg-gray-200 text-gray-500 cursor-not-allowed"
                }`}
              >
                {actionLoading === "save" ? "저장 중..." : "수정 저장"}
              </button>
            )}
            {isPageCommittable && (
              <button
                onClick={handleCommitPage}
                disabled={!!actionLoading || dirtyPages.has(activePageId)}
                className={`px-4 py-2 rounded-md text-sm ${
                  actionLoading === "commit-all"
                    ? "bg-blue-100 text-blue-300 cursor-not-allowed"
                    : !dirtyPages.has(activePageId)
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-gray-200 text-gray-500 cursor-not-allowed"
                }`}
                title={dirtyPages.has(activePageId) ? "먼저 수정 사항을 저장해주세요" : ""}
              >
                {actionLoading === "commit-page" || actionLoading === "commit-all"
                  ? "등록 중..."
                  : "등록 확정"}
              </button>
            )}
            {stagedCount > 1 && (
              <button
                onClick={handleCommitAll}
                disabled={!!actionLoading}
                className={`px-4 py-2 rounded-md text-sm ${
                  actionLoading === "commit-all"
                    ? "bg-indigo-100 text-indigo-300 cursor-not-allowed"
                    : "bg-indigo-600 text-white hover:bg-indigo-700"
                }`}
              >
                {actionLoading === "commit-all" ? "등록 중..." : `전체 등록 (${stagedCount})`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
